var __jsx = React.createElement;
import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AwesomeIcon from "../../AwesomeIcon";
import { faArrowRight, faCheckCircle } from "@fortawesome/pro-solid-svg-icons";
var RequiredFieldsStatus = function RequiredFieldsStatus(_ref) {
  var requiredEmptyFields = _ref.requiredEmptyFields,
    requiredEmptyCount = _ref.requiredEmptyCount,
    requiredFieldsWithErrors = _ref.requiredFieldsWithErrors,
    fieldsWithErrorsCount = _ref.fieldsWithErrorsCount,
    onHighlightChange = _ref.onHighlightChange,
    expandSectionRef = _ref.expandSectionRef;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(null),
    lastJumpedFieldName = _useState[0],
    setLastJumpedFieldName = _useState[1];

  // Handle jump to next required field (empty or with errors)
  var handleJumpToNext = useCallback(function () {
    // Determine which field list to use based on priority
    var fieldsToJump = requiredEmptyCount > 0 ? requiredEmptyFields : requiredFieldsWithErrors;
    var fieldCount = requiredEmptyCount > 0 ? requiredEmptyCount : fieldsWithErrorsCount;
    if (fieldCount === 0) return;

    // Find the next field to jump to
    var nextField = null;
    if (lastJumpedFieldName) {
      // Find the position of the last jumped field
      var currentFieldIndex = fieldsToJump.findIndex(function (f) {
        return f.field_name === lastJumpedFieldName;
      });
      if (currentFieldIndex >= 0) {
        // Last jumped field still exists in the list, get the next one
        nextField = fieldsToJump[currentFieldIndex + 1] || fieldsToJump[0];
      } else {
        // Last jumped field was filled/removed, start from beginning
        nextField = fieldsToJump[0];
      }
    } else {
      // First time jumping, start from beginning
      nextField = fieldsToJump[0];
    }
    if (nextField) {
      // Update last jumped field name
      setLastJumpedFieldName(nextField.field_name);

      // Check if we need to expand the section containing this field
      var needsExpansion = false;
      if (nextField.section && expandSectionRef.current) {
        // Transform section name to match the ID format used by useFieldGrouping
        // (lowercase with underscores instead of spaces)
        var sectionId = nextField.section.toLowerCase().replace(/\s+/g, "_");

        // Check if section is already expanded
        if (!expandSectionRef.current.isExpanded(sectionId)) {
          // Section is collapsed, expand it
          expandSectionRef.current.expand(sectionId);
          needsExpansion = true;
        }
      }

      // Helper function to scroll and highlight the field
      var scrollAndHighlight = function scrollAndHighlight() {
        // Find the field element by data attribute and scroll to it
        var fieldElement = document.querySelector("[data-field-name=\"".concat(nextField.field_name, "\"]"));
        if (fieldElement) {
          // Find the scrollable container (the element with overflow)
          var scrollContainer = fieldElement.closest(".overflow-hidden, .overflow-auto, .overflow-y-auto, .overflow-scroll");

          // Check if element is already in viewport relative to its scroll container
          var rect = fieldElement.getBoundingClientRect();
          var isInView = false;
          if (scrollContainer) {
            var containerRect = scrollContainer.getBoundingClientRect();
            isInView = rect.top >= containerRect.top && rect.bottom <= containerRect.bottom && rect.left >= containerRect.left && rect.right <= containerRect.right;
          } else {
            // Fallback to window if no scroll container found
            isInView = rect.top >= 0 && rect.left >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && rect.right <= (window.innerWidth || document.documentElement.clientWidth);
          }

          // Helper function to highlight and focus
          var highlightAndFocus = function highlightAndFocus() {
            onHighlightChange(nextField.field_name);

            // Focus on the input if possible
            var inputElement = fieldElement.querySelector("input, select, textarea");
            if (inputElement instanceof HTMLElement) {
              inputElement.focus();
            }

            // Clear highlight after 3 seconds
            setTimeout(function () {
              onHighlightChange(null);
            }, 3000);
          };
          if (isInView) {
            // Element is already visible, highlight immediately
            highlightAndFocus();
          } else {
            // Element needs scrolling, scroll first then highlight
            fieldElement.scrollIntoView({
              behavior: "smooth",
              block: "center"
            });

            // Wait for scroll animation to complete before highlighting
            // Smooth scroll typically takes 500-800ms, we wait 700ms to be safe
            setTimeout(highlightAndFocus, 700);
          }
        }
      };

      // If section needed expansion, wait for DOM update; otherwise scroll immediately
      if (needsExpansion) {
        // Wait 100ms for section expansion animation to complete
        setTimeout(scrollAndHighlight, 100);
      } else {
        // Section already expanded, scroll immediately
        scrollAndHighlight();
      }
    }
  }, [lastJumpedFieldName, requiredEmptyCount, requiredEmptyFields, requiredFieldsWithErrors, fieldsWithErrorsCount, onHighlightChange, expandSectionRef]);
  return /*#__PURE__*/React.createElement("div", {
    className: "h-full"
  }, requiredEmptyCount > 0 ? /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-3 px-2 py-2 rounded-2xl bg-yellow-100 dark:bg-yellow-800/20 border border-amber-600 dark:border-amber-400/50"
  }, /*#__PURE__*/React.createElement("span", {
    className: "w-6 h-6 flex items-center justify-center rounded-full bg-yellow-700 dark:bg-yellow-500 text-white dark:text-gray-900 text-md font-bold"
  }, requiredEmptyCount), /*#__PURE__*/React.createElement("span", {
    className: "text-yellow-700 dark:text-yellow-400/80 text-md font-semibold"
  }, t("label.required_fields_left")), /*#__PURE__*/React.createElement("span", {
    className: "text-yellow-700 dark:text-yellow-400/80"
  }, " | "), /*#__PURE__*/React.createElement("button", {
    onClick: handleJumpToNext,
    disabled: requiredEmptyCount === 0,
    className: "py-2 text-teal-500 dark:text-teal-400 text-md pr-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed",
    "aria-label": "Jump to next required field"
  }, t("button.jump_to_next"), " ", /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faArrowRight,
    size: "sm"
  }))) : fieldsWithErrorsCount > 0 ? /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-3 px-2 py-2 rounded-2xl bg-red-100 dark:bg-red-900/20 border border-red-600 dark:border-red-400/50"
  }, /*#__PURE__*/React.createElement("span", {
    className: "w-6 h-6 flex items-center justify-center rounded-full bg-red-600 dark:bg-red-500 text-white dark:text-gray-900 text-md font-semibold"
  }, fieldsWithErrorsCount), /*#__PURE__*/React.createElement("span", {
    className: "text-red-700 dark:text-red-400/80 text-md font-semibold"
  }, t("label.required_fields_with_errors")), /*#__PURE__*/React.createElement("span", {
    className: "text-red-700 dark:text-red-400/80"
  }, " | "), /*#__PURE__*/React.createElement("button", {
    onClick: handleJumpToNext,
    disabled: fieldsWithErrorsCount === 0,
    className: "py-2 text-teal-500 dark:text-teal-400 text-md pr-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed",
    "aria-label": "Jump to next field with error"
  }, t("button.jump_to_next"), " ", /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faArrowRight,
    size: "sm"
  }))) : /*#__PURE__*/React.createElement("div", {
    className: "flex items-center h-[34px] gap-3 py-2 pl-2 pr-3 rounded-2xl bg-[#C9EFDE] dark:bg-[#053D2B] border border-[#007A43] dark:border-[#5DDAA2]"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faCheckCircle,
    className: "h-6 w-6 text-[#007A43] dark:text-[#5DDAA2]"
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-[#007A43] dark:text-[#5DDAA2] text-md font-semibold"
  }, t("label.all_required_fields_are_completed"))));
};
export default RequiredFieldsStatus;